import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import DashboardShell from '@/components/DashboardShell';
import { Button } from '@/components/ui/button';
import {
  DollarSign, TrendingUp, Calendar, AlertCircle,
  Loader2, ExternalLink, CheckCircle, Clock, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Earnings Card Component
const EarningsJobCard = ({ job }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const formatCurrency = (amount, currency) => {
    const symbols = { GBP: '£', USD: '$', EUR: '€' };
    return `${symbols[currency] || currency}${amount.toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4 hover:border-zinc-300 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-medium text-zinc-900">{job.category}</h3>
          <p className="text-sm text-zinc-500">{formatDate(job.completed_at)}</p>
        </div>
        <span className="px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded-full">
          Completed
        </span>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-zinc-500">Job total</span>
          <span className="text-zinc-900">{formatCurrency(job.gross_amount, job.currency)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-zinc-500">Platform fee ({job.platform_fee_percent}%)</span>
          <span className="text-red-500">-{formatCurrency(job.platform_fee_amount, job.currency)}</span>
        </div>
        <hr className="border-zinc-100" />
        <div className="flex justify-between font-medium">
          <span className="text-zinc-900">Your payout</span>
          <span className="text-green-600">{formatCurrency(job.net_payout, job.currency)}</span>
        </div>
      </div>
    </div>
  );
};

// Stripe Connect Status Component
const StripeConnectStatus = ({ status }) => {
  const configs = {
    not_started: {
      icon: AlertCircle,
      color: 'text-orange-500',
      bg: 'bg-orange-50',
      title: 'Set up payouts',
      description: 'Connect your bank account to receive payouts',
      action: 'Get Started'
    },
    pending: {
      icon: Clock,
      color: 'text-yellow-500',
      bg: 'bg-yellow-50',
      title: 'Verification pending',
      description: 'Complete your Stripe verification to enable payouts',
      action: 'Continue Setup'
    },
    active: {
      icon: CheckCircle,
      color: 'text-green-500',
      bg: 'bg-green-50',
      title: 'Payouts enabled',
      description: 'Your bank account is connected and verified',
      action: null
    },
    restricted: {
      icon: AlertCircle,
      color: 'text-red-500',
      bg: 'bg-red-50',
      title: 'Account restricted',
      description: 'Action required - please update your account details',
      action: 'Fix Issues'
    }
  };
  
  const config = configs[status?.connect_status] || configs.not_started;
  const Icon = config.icon;

  return (
    <div className={`rounded-xl p-4 ${config.bg}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-5 h-5 ${config.color} mt-0.5`} />
        <div className="flex-1">
          <h3 className={`font-medium ${config.color}`}>{config.title}</h3>
          <p className="text-sm text-zinc-600 mt-1">{config.description}</p>
          {config.action && (
            <Button
              size="sm"
              className="mt-3 bg-zinc-900 hover:bg-zinc-800"
              data-testid="stripe-connect-btn"
            >
              {config.action}
              <ExternalLink className="w-3.5 h-3.5 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const ContractorEarningsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    try {
      const response = await axios.get(`${API_URL}/contractor/earnings`);
      setEarnings(response.data);
    } catch (error) {
      toast.error('Failed to load earnings');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€' };
    return `${symbols[currency] || currency}${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <DashboardShell>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
        </div>
      </DashboardShell>
    );
  }

  const summary = earnings?.summary || {
    total_gross: 0,
    total_platform_fees: 0,
    total_net_payout: 0,
    jobs_count: 0,
    currency: 'GBP'
  };

  return (
    <DashboardShell>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Earnings & Payouts</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Track your earnings and manage payouts
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-zinc-200 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-sm text-zinc-500">Total Earnings</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900">
              {formatCurrency(summary.total_net_payout, summary.currency)}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              After platform fees
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl border border-zinc-200 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm text-zinc-500">Gross Revenue</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900">
              {formatCurrency(summary.total_gross, summary.currency)}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              {summary.jobs_count} completed jobs
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl border border-zinc-200 p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-zinc-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-zinc-600" />
              </div>
              <span className="text-sm text-zinc-500">Platform Fees</span>
            </div>
            <p className="text-3xl font-bold text-zinc-900">
              {formatCurrency(summary.total_platform_fees, summary.currency)}
            </p>
            <p className="text-sm text-zinc-500 mt-1">
              6% per job
            </p>
          </motion.div>
        </div>

        {/* Stripe Connect Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl border border-zinc-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-zinc-100">
            <h2 className="text-lg font-semibold text-zinc-900">Payout Settings</h2>
          </div>
          <div className="p-6">
            <StripeConnectStatus status={earnings?.stripe_connect} />
            
            <div className="mt-4 p-4 bg-zinc-50 rounded-lg">
              <h3 className="font-medium text-zinc-900 mb-2">Payout Schedule</h3>
              <p className="text-sm text-zinc-600">
                Payouts are processed weekly on Fridays. Funds typically arrive within 2-3 business days.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Recent Earnings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl border border-zinc-200 overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-900">Earnings History</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/contractor/jobs')}
              className="text-zinc-600"
            >
              View all
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          
          <div className="p-6 space-y-4">
            {earnings?.earnings_by_job?.length > 0 ? (
              earnings.earnings_by_job.slice(0, 5).map((job, i) => (
                <EarningsJobCard key={job.job_id || i} job={job} />
              ))
            ) : (
              <div className="text-center py-8">
                <DollarSign className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                <p className="text-zinc-500">No earnings yet</p>
                <p className="text-sm text-zinc-400 mt-1">
                  Complete jobs to start earning
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Fee Explanation */}
        <div className="bg-zinc-50 rounded-xl p-6">
          <h3 className="font-semibold text-zinc-900 mb-3">Understanding Your Earnings</h3>
          <div className="space-y-3 text-sm text-zinc-600">
            <p>
              <strong>ZOLT is free for customers.</strong> We charge a 6% platform fee on contractor earnings
              to cover payment processing, insurance, and platform operations.
            </p>
            <div className="grid md:grid-cols-3 gap-4 mt-4">
              <div className="bg-white rounded-lg p-4">
                <p className="font-medium text-zinc-900">Customer pays</p>
                <p className="text-zinc-500">Full job amount</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="font-medium text-zinc-900">Platform fee</p>
                <p className="text-zinc-500">6% deducted</p>
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="font-medium text-zinc-900">You receive</p>
                <p className="text-zinc-500">94% of job total</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default ContractorEarningsPage;
